import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { useGeneralDataContext } from '../../contexts/useGeneralDataContext/useGeneralDataContext';
import useGetExhibitions from '../../hooks/useGetExhibitions/useGetExhibitions';
import useSetPageTitle from '../../hooks/useSetPageTitle/useSetPageTitle';

import ExhibitionItem from './components/ExhibitionItem';
import PageBanner from '../../common/PageBanner/PageBanner';
import Pagination from '../../common/Pagination/Pagination';
import Spinner from '../../components/Spinner/Spinner';

import { TExhibition } from '../../types/TGetExhibitionsResponse';
import {
  DEFAULT_IMAGE_ALT_TEXT,
  DEV_BASE_URL,
  IS_DEV,
  LISTING_PAGE_OFFSET,
  PROD_BASE_URL,
  UPLOADS_FOLDER,
} from '../../config/constants/contants';

import styles from './Exhibitions.module.scss';
import commons from '../../shared/styles/common.module.scss';

const Exhibitions = () => {
  const { t } = useTranslation();
  const [page, setPage] = React.useState<number>(1);

  const imagesURL = IS_DEV ? DEV_BASE_URL : PROD_BASE_URL;

  useSetPageTitle({
    pageTitle: `${t('pages.exhibitions')} - ${t('general.website-name')}`,
  });

  const { language } = useGeneralDataContext();

  const {
    data: exhibitionsData,
    isLoading: isLoadingExhibitions,
    refetch,
  } = useGetExhibitions(LISTING_PAGE_OFFSET, language, page);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  React.useEffect(() => {
    if (exhibitionsData) {
      scrollToTop();
    }
  }, [exhibitionsData]);

  React.useEffect(() => {
    refetch();
  }, [page]);

  const gridTemplateRows = React.useMemo(() => {
    if (exhibitionsData && exhibitionsData.posts) {
      if (exhibitionsData.posts.length < LISTING_PAGE_OFFSET) {
        return Math.ceil(exhibitionsData.posts.length / 2);
      }
    }

    return 5;
  }, [exhibitionsData]);

  return (
    <div>
      <PageBanner
        imageSrc={`${imagesURL}${UPLOADS_FOLDER}/cetatea-fagaras-sala-tronului.jpeg`}
        imageText={DEFAULT_IMAGE_ALT_TEXT}
      />
      <div
        className={classNames(styles.exhibitionsContainer, commons.container)}>
        <div className={styles.headingWrapper}>
          <h3 className={commons.subSectionHeading}>
            {t('pages.exhibitions')}
          </h3>
          <hr className={styles.headingLine} />
        </div>
        {isLoadingExhibitions ? (
          <Spinner variant='brown' />
        ) : (
          <div
            className={styles.exhibitionsList}
            style={{
              gridTemplateRows: `repeat(${gridTemplateRows}, 430px)`,
            }}>
            {exhibitionsData?.posts.map((exhibition: TExhibition) => (
              <ExhibitionItem key={exhibition.id} exhibition={exhibition} />
            ))}
          </div>
        )}
        <div className={styles.seeMoreWrapper}>
          <Pagination
            colorVariant='brown'
            currentPage={page}
            pageSize={LISTING_PAGE_OFFSET}
            totalCount={exhibitionsData?.total_number || 0}
            siblingCount={2}
            onPageChange={(currentPage) => setPage(currentPage)}
          />
        </div>
      </div>
    </div>
  );
};

export default Exhibitions;
